/**
 * What one equivalent group's price spread is worth, in the group's own
 * comparison unit.
 *
 * The Related Products card answers a buying question: "if I bought this the
 * cheapest way instead of the dearest, what would I save per unit?" That is
 * the spread between the lowest and the highest COMPARABLE unit cost in the
 * group — never between purchase prices, which describe different packs, and
 * never across two dimensions (see priceComparison: an option that cannot
 * reach the group's denominator has no comparable cost and never lands here).
 *
 * Nothing in this module rounds toward zero. A half-cent saving on a cocktail
 * pick is a real number and prints as one.
 */

import { roundHalfUpFixed } from './priceComparison';

const positive = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Decimals that show two significant figures of `value`, clamped to [2, 4].
 * $3.48 stays $3.48; a $0.00526 spread prints $0.0053 rather than collapsing
 * to $0.01 (overstated) or $0.00 (a lie).
 */
export function savingsDecimals(value) {
  const amount = positive(value);
  if (amount == null) return 2;
  const wanted = 1 - Math.floor(Math.log10(amount));
  return Math.min(4, Math.max(2, wanted));
}

/** "$0.0053" / "$3.48" — trailing zeros past 2dp trimmed, never "$0.00". */
export function formatSavingsAmount(value) {
  const amount = positive(value);
  if (amount == null) return null;
  let text = roundHalfUpFixed(amount, savingsDecimals(amount));
  if (text.includes('.')) {
    const [whole, frac = ''] = text.split('.');
    const trimmed = frac.replace(/0+$/, '');
    const kept = trimmed.length >= 2 ? trimmed : frac.slice(0, 2);
    text = `${whole}.${kept}`;
  }
  return `$${text}`;
}

/**
 * The spread across one card's comparable rows.
 *
 * @param {Array} rows [{ optionId, comparableUnitCost }] — rows WITHOUT a
 *   comparable cost are simply absent; pass them in and they are ignored.
 * @param {object} [opts] { blocked } — `blocked` is the caller's gate for
 *   "some priced option on this card could not be compared", in which case no
 *   row may claim to be lowest and no saving may be quoted: the vendor nobody
 *   could rank might be the real winner.
 * @returns {{
 *   valid: boolean, lowestOptionIds: string[], highestOptionId: string|null,
 *   lowestCost: number|null, highestCost: number|null,
 *   savingsPerUnit: number|null, savingsPercent: number|null, tied: boolean
 * }}
 */
export function buildGroupSavings(rows = [], { blocked = false } = {}) {
  const none = {
    valid: false,
    lowestOptionIds: [],
    highestOptionId: null,
    lowestCost: null,
    highestCost: null,
    savingsPerUnit: null,
    savingsPercent: null,
    tied: false,
  };
  if (blocked) return none;
  const priced = (Array.isArray(rows) ? rows : [])
    .map((row) => ({ optionId: String(row?.optionId ?? ''), cost: positive(row?.comparableUnitCost) }))
    .filter((row) => row.optionId && row.cost != null);
  if (priced.length < 2) return none;

  const lowestCost = Math.min(...priced.map((r) => r.cost));
  const highestCost = Math.max(...priced.map((r) => r.cost));
  const lowestOptionIds = priced.filter((r) => r.cost === lowestCost).map((r) => r.optionId);
  const highest = priced.find((r) => r.cost === highestCost) || null;

  // Everything costs the same per unit: a true tie, and there is nothing to
  // save. Still "valid" — the card may say so — but no winner is crowned.
  if (highestCost === lowestCost) {
    return {
      ...none,
      valid: true,
      lowestOptionIds,
      lowestCost,
      highestCost,
      highestOptionId: highest?.optionId ?? null,
      tied: true,
    };
  }

  return {
    valid: true,
    lowestOptionIds,
    highestOptionId: highest?.optionId ?? null,
    lowestCost,
    highestCost,
    savingsPerUnit: highestCost - lowestCost,
    savingsPercent: ((highestCost - lowestCost) / highestCost) * 100,
    tied: lowestOptionIds.length > 1,
  };
}

/**
 * "Save $0.0053/each · 35% vs highest" — the dollar figure leads because that
 * is the number a buyer acts on; the percentage is context, and is dropped
 * when it would round to 0%.
 */
export function formatSavingsLine(savings, unitLabel) {
  if (!savings?.valid || savings.savingsPerUnit == null) return null;
  const amount = formatSavingsAmount(savings.savingsPerUnit);
  if (!amount) return null;
  const per = unitLabel ? `${amount}/${unitLabel}` : amount;
  const percent = Math.round(savings.savingsPercent);
  return percent >= 1 ? `Save ${per} · ${percent}% vs highest` : `Save ${per} vs highest`;
}
